<template>
    <div class="s-purchasing a-container">
        <section :id="id" class="s-slice">
            <h1 v-if="productName" class="product-name">
                {{ productName }}
            </h1>
            <h2 v-if="title" class="title">
                {{ title }}
            </h2>
            <p v-if="lead" class="lead">
                {{ lead }}
            </p>
            <p v-if="hasPromoDescription" class="promo">
                <a-glyph v-if="promo.glyph" :name="promo.glyph" size="m" />
                {{ promo.description }}
            </p>

            <div class="wrapper">
                <div
                    v-if="!isLoaded"
                    v-loading="true"
                    class="loader"
                    el-loading-size="48"
                    el-loading-color="brand-primary"
                />

                <div class="content">
                    <div class="products">
                        <div
                            v-for="(product, index) in productList"
                            :key="`product-${index}`"
                            :class="classProduct(product, index)"
                            @click="slice.selectedProduct = index"
                        >
                            <div>
                                <a-label v-if="product.label" v-bind="product.label">
                                    {{ product.label.text }}
                                </a-label>

                                <p
                                    v-if="product.title"
                                    class="product-title set-min-height"
                                    :style="{'min-height': minHeight[0]}"
                                    @click="product.isOpened = !product.isOpened"
                                >
                                    {{ product.title }}
                                    <a-glyph v-if="isMobile" name="i-chevron-big-right--16" size="m" />
                                </p>
                            </div>

                            <div
                                class="product-item-wrapper set-min-height"
                                :style="{'min-height': minHeight[1]}"
                            >
                                <p
                                    class="message set-min-height"
                                    :style="{'min-height': minHeight[2]}"
                                    v-html="product.message"
                                />

                                <div class="selector">
                                    <component
                                        :is="`s-purchasing-${name}`"
                                        v-bind="current(product)"
                                        v-for="(name, nameIndex) in listSelector(product)"
                                        :key="`s-purchasing-${nameIndex}`"
                                        @change="handleChange(name, index, $event)"
                                    />
                                </div>

                                <p v-if="isBenefitVisible(product)" class="benefit" v-html="product.benefit" />

                                <div v-if="hasMaxQuantity(product)" class="max-quantity">
                                    <a-glyph name="i-discount--16" />
                                    <p class="max-quantity-title">
                                        {{ dictionary(`${product.selected.period}Y-title`) }}
                                    </p>
                                    <p
                                        v-for="(item, i) in listPeriod(product)"
                                        :key="`max-quantity-${i}`"
                                        class="max-quantity-item"
                                    >
                                        <span v-html="item.text" />
                                        <span v-html="pricingList(item)" />
                                    </p>
                                </div>

                                <div v-if="product.addon" class="selector-addon">
                                    <s-purchasing-quantity
                                        v-bind="current(product.addon)"
                                        @change="handleChangeAddon(index, $event)"
                                    />
                                </div>
                            </div>

                            <ul
                                v-if="product.information"
                                class="information set-min-height"
                                :style="{'min-height': minHeight[3]}"
                            >
                                <li
                                    v-for="(item, itemIndex) in product.information"
                                    :key="`information-${itemIndex}`"
                                    v-html="item"
                                />
                            </ul>
                        </div>
                    </div>
                    <p v-if="slice.description" class="description" v-html="slice.description" />
                </div>

                <s-purchasing-order
                    :promo="promo"
                    :slice="slice"
                    :settings="settings"
                    :product-list="productList"
                    @remove="handleClickRemove($event)"
                />
            </div>
        </section>
        <s-awards
            v-if="slice?.awards?.length"
            :background="background"
            :award_ids="slice.awards"
        />
    </div>
</template>

<script>
import Loading from '@uikit/ui-kit/packages/loading/src/directive';
import { cloneDeep, merge, isInteger } from 'lodash';
import Vue from 'vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ALabel from '@core/components/label/label.vue';
import analytics from '@core/mixins/analytics.js';
import breakpoint from '@core/mixins/breakpoint.js';
import contentMixin from '@core/mixins/content.js';
import currency from '@core/mixins/currency.js';
import purchasing from '@core/mixins/purchasing.js';
import styleMixin from '@core/mixins/style.js';
import SAwards from '@core/slices/pages/awards/awards.vue';
import SPurchasingOrder from './order.vue';
import SPurchasingPeriod from './period.vue';
import SPurchasingQuantity from './quantity.vue';

export default {
    name: 'SPurchasingItems2',

    components: {
        AGlyph,
        ALabel,
        SAwards,
        SPurchasingOrder,
        SPurchasingPeriod,
        SPurchasingQuantity,
    },

    directives: {
        Loading,
    },

    mixins: [breakpoint, contentMixin, styleMixin, purchasing, currency, analytics],

    props: {
        title: {
            type: String,
            default: null,
        },
        lead: {
            type: String,
            default: null,
        },
        background: {
            type: String,
            default: '',
        },
        settings: {
            type: Object,
            default: null,
        },
        purchasePageCTA: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            coupon: '',
            promo: {},
            slice: {},
            productList: [],
            isLoaded: false,
            minHeight: [],
        };
    },

    computed: {
        isPromoActive() {
            return this.promo.isPromoActive;
        },
        priceDiscount() {
            if (!this.isPromoActive || !this.discount) return '';
            const label = this?.promo?.label || '';
            return label.replace('XX%', this.setCurrency(this.discount));
        },

        productName() {
            if (this.slice?.withoutProductName) return '';
            return this.productItems[this.internalProductId]?.name || '';
        },

        hasPromoDescription() {
            if (this.slice?.withoutPromoDescription) return false;
            return this.isPromoActive && this.promo.description;
        },
    },

    created() {
        if (!this.settings.products) return;
        this.slice = cloneDeep(this.settings);
        this.productList = cloneDeep(this.settings.products);
        this.productList.forEach((product, index) => {
            this.updateProductPrice(product, index);
            if (product.addon) this.updateProductPrice(product.addon, index);
        });
    },

    mounted() {
        this.coupon = this.$route.query?.coupon || '';

        if (this.coupon && this.activeCoupon?.discount) {
            const list = this.activeCoupon.cleverBridgeCartList || '';
            this.promo = {
                ...this.promo,
                ...this.activeCoupon,
                isPromoActive: true,
                cleverBridgeCartList: list.split(',').map(Number),
            };
        } else {
            const isPurchase = true;
            const cta = merge(this.productCTA({ isPurchase }), this.purchasePageCTA);
            const isPromoActive = !cta.doNotApplyPromo && this.checkPromo({ ...cta.promo, isPurchase });

            this.promo = { ...cta.promo, isPurchase, isPromoActive };
        }

        this.setMinHeight();
        window.addEventListener('resize', this.setMinHeight);
        this.isLoaded = true;
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.setMinHeight);
    },

    methods: {
        current(product) {
            let hasPromo = false;
            let price = product.priceMix || product.price;
            let discount = product.discountMix || product.discount;

            if (this.isPromoActive) {
                const promoList = this.promo.cleverBridgeCartList || [];
                if (promoList.includes(product.cleverBridgeCartID)) {
                    hasPromo = true;
                }
                if (hasPromo && !discount) {
                    const license = this.findLicense(product);
                    price = license.price;
                    discount = license.discount;
                }
            }

            return {
                price: this.setCurrency(price),
                discount: hasPromo ? this.setCurrency(price + discount) : null,
                dictionary: this.settings.dictionary,
                licenses: product.licenses,
                period: product.selected.period,
                quantity: product.quantity,
                selector: product.selector,
                value: product.selected.quantity,
            };
        },

        async setMinHeight() {
            this.minHeight = [];

            if (!this.isDesktop) {
                return;
            }

            await this.$nextTick();

            const list = document.querySelectorAll('.set-min-height');
            const result = [...list].map((item) => item.offsetHeight);
            const size = result.length / 2;
            result.slice(0, size).forEach((item, i) => {
                Vue.set(this.minHeight, i, `${Math.max(item, result.slice(size)[i])}px`);
            });
        },

        hasMaxQuantity(product) {
            return product.licenses.find((item) => item.maxQuantity);
        },

        isBenefitVisible(product) {
            return !this.isPromoActive && product.benefit;
        },

        classProduct(product, index) {
            return [
                'product-item',
                { 'product-item-close': this.isMobile && !product.isOpened },
                { 'product-item-active': this.slice.selectedProduct === index },
            ];
        },

        dictionary(item) {
            return this.dictionary?.[item] || this.settings?.dictionary?.[item] || item;
        },

        findLicense(product) {
            const cleverBridgeCartID = product?.cleverBridgeCartID || null;
            const license = this.licenseData({ cleverBridgeCartID, promo: this.promo });
            return { price: license.price || 0, discount: license.discount_absolute || 0 };
        },

        listPeriod(product) {
            const period = Number(product.selected.period);
            return product.licenses.filter((item) => item.period === period);
        },

        listSelector(product) {
            return product.selector?.list || ['quantity', 'period'];
        },

        updateProductPrice(item, index) {
            if (!isInteger(index)) return;
            const licenses = item?.licenses || [];
            const period = Number(item.period || item.selected.period);
            const product = item.isAddon ? this.productList[index].addon : this.productList[index];

            let current = licenses.find((license) => license.period === period) || item;

            if (this.hasMaxQuantity(item)) {
                const compare = (license) => item.selected.quantity < license.maxQuantity;
                current = this.listPeriod(item).find(compare);
            }

            const listMix = [];
            const list = product.quantity?.list || [];
            let quantity = product.selected.quantity;
            list.forEach((seats) => {
                const remainder = quantity % seats;
                const count = (quantity - remainder) / seats;
                if (count > 0) {
                    const compare = (l) => l.period === period && l.quantity === seats;
                    const license = product.licenses.find(compare);
                    listMix.push({
                        seats,
                        quantity: count,
                        cleverBridgeCartID: license.cleverBridgeCartID,
                        ...this.findLicense(license),
                    });
                }
                if (!remainder) quantity -= count * quantity;
                if (quantity > seats) quantity = remainder;
            });

            const { price, discount } = this.findLicense(current);

            const priceMix = listMix.map((license) => license.price * license.quantity);
            product.price = price;
            product.priceMix = priceMix.reduce((sum, value) => sum + value, 0);

            const discountMix = listMix.map((license) => license.discount * license.quantity);
            product.discount = discount;
            product.discountMix = discountMix.reduce((sum, value) => sum + value, 0);

            product.selected.list = listMix;
            product.cleverBridgeCartID = current?.cleverBridgeCartID || '';
        },

        pricingList(product) {
            const { price, discount } = this.findLicense(product);
            const priceHTML = `<span>${this.setCurrency(price)}</span>`;
            const priceFullHTML = `<del>${this.setCurrency((price + discount))}</del>`;
            return discount ? `${priceFullHTML}${priceHTML}` : priceHTML;
        },

        isQuantityHidden(index) {
            const product = this.productList[index];
            return product.quantity.isHidden;
        },

        handleChange(type, index, event) {
            const quantity = Number(event);
            const product = this.productList[index];
            product.selected[type] = quantity;

            if (this.isQuantityHidden(index)) {
                product.selected.quantity = quantity ? 1 : 0;
            }

            if (product.addon && !product.selected.period) {
                this.handleChangeAddon(index, 0);
            }

            this.updateProductPrice(product, index);
        },

        handleChangeAddon(index, quantity) {
            const product = this.productList[index];
            const isSelected = product.selected.period && product.selected.quantity;

            if (quantity && !isSelected) {
                product.selected.period = 1;
                product.selected.quantity = 1;
                this.updateProductPrice(product, index);
            }

            product.addon.selected.quantity = quantity;
            this.updateProductPrice(product.addon, index);
        },

        handleClickRemove(item) {
            const index = this.productList.findIndex((p) => p.cleverBridgeCartID === item.id);

            if (item.isAddon) {
                this.handleChangeAddon(index, 0);
                return;
            }

            const product = this.productList[index];
            product.selected.quantity = 0;

            if (product.addon) {
                product.selected.period = 0;
                product.addon.selected.quantity = 0;
            }

            const current = product.addon ? product.addon : product;
            this.updateProductPrice(current, index);
        },
    },

    async serverPrefetch() {
        if (!this.settings.products) return;
        await Promise.all([
            this.$store.dispatch('slices/getSyncedData', { slice: 's-loader', locale: this.locale }),
            this.findCleverBridgeCart(this.settings.products, { ...this.purchasePageCTA, isPurchase: true }),
        ]);
    },
};
</script>

<style lang="postcss" scoped>
.s-purchasing {
    padding-top: 104px;
    &:deep(.s-slice) {
        padding: 0;
    }
    &:deep(.s-awards) {
        margin: 0 -16px;
        margin-top: 64px;
        padding: 48px 32px;
        @media (--viewport-mobile-wide) {
            margin-inline-end: -32px;
            margin-inline-start: -32px;
        }
        @media (--viewport-desktop) {
            padding: 48px 64px;
        }
        @media (--viewport-desktop-wide) {
            margin-inline-end: -64px;
            margin-inline-start: -64px;
        }
        .a-container {
            padding: 0;
        }
        .list {
            gap: 48px;
            flex-direction: column;
            @media (--viewport-desktop) {
                gap: 64px;
                flex-wrap: nowrap;
                flex-direction: row;
            }
            .item {
                max-width: unset;
                @media (--viewport-desktop) {
                    width: 50%;
                    display: flex;
                    align-items: center;
                }
            }
            .award {
                align-items: center;
                flex-direction: column-reverse;
                .image-wrapper {
                    width: 100%;
                    max-width: 284px;
                    justify-content: center;
                    @media (--viewport-desktop) {
                        max-width: 214px;
                    }
                }
                @media (--viewport-tablet) {
                    flex-direction: row-reverse;
                }
                @media (--viewport-desktop) {
                    flex-direction: row-reverse;
                    justify-content: flex-end;
                }
            }
            .title {
                @mixin lead;
                font-weight: 400;
                text-align: center;
                @media (--viewport-tablet) {
                    width: 100%;
                    text-align: start;
                }
                @media (--viewport-desktop) {
                    width: 50%;
                }
            }
            .image-wrapper {
                @media (--viewport-desktop) {
                    width: 50%;
                }
            }
            .image {
                height: 80px;
                display: flex;
                align-items: center;
            }
        }
    }
}
.loader {
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    background: #fff;
    position: absolute;
    & ~ .content {
        opacity: 0;
    }
}
.product-name {
    @mixin large;
    color: var(--av-nav-primary);
    & ~ .title {
        margin-top: 56px;
        font-weight: 400;
    }
}
.title {
    @mixin display-accent;
    color: var(--av-nav-primary);
    @media (--viewport-desktop) {
        @mixin hero-accent;
    }
}
.lead {
    @mixin paragraph;
    margin-top: 16px;
    color: var(--av-fixed-secondary);
    @media (--viewport-desktop) {
        @mixin lead;
        max-width: 798px;
    }
    @media (--viewport-desktop-wide) {
        max-width: 1054px;
    }
}
.promo {
    @mixin paragraph-accent;
    margin-top: 32px;
    margin-bottom: -24px;
    color: var(--av-fixed-success);
    .a-glyph {
        margin-inline-end: 8px;
        fill: var(--av-fixed-success);
    }
}
.wrapper {
    margin: 48px 0 0;
    position: relative;
    @media (--viewport-tablet) {
        gap: 16px;
        display: flex;
        margin: 40px 0 0;
        justify-content: space-between;
    }
    @media (--viewport-desktop-large) {
        gap: 40px;
    }
}
.content {
    @media (--viewport-tablet) {
        width: 50%;
    }
    @media (--viewport-desktop) {
        width: 100%;
    }
}
.products {
    color: var(--av-nav-primary);
    background: var(--av-inversed-primary);
    @media (--viewport-desktop) {
        display: flex;
        border-radius: 4px;
        border: 1px solid var(--av-brand-light);
        box-shadow: 0 4px 8px rgba(36, 49, 67, 0.1);
    }
}
.product-item {
    display: flex;
    padding: 0 16px;
    user-select: none;
    flex-direction: column;
    border-radius: 4px;
    border: 1px solid var(--av-brand-light);
    box-shadow: 0 4px 8px rgba(36, 49, 67, 0.1);
    @media (--viewport-tablet) {
        padding: 0 24px;
    }
    @media (--viewport-desktop) {
        width: 50%;
        border: none;
        box-shadow: none;
        border-radius: unset;
        &:not(:first-child) {
            border-top: none;
            border-inline-start: 1px solid var(--av-brand-light);
        }
    }
    &:not(:first-child) {
        margin-top: 16px;
        @media (--viewport-desktop) {
            margin: 0;
        }
    }
    &:deep(.a-label) {
        top: 0;
        position: absolute;
        margin-top: -8px;
        &.size-l {
            margin-top: -12px;
        }
    }
}
.product-item-active {
    background: var(--av-solid-brand-lightest);
}
.product-item-close {
    .product-title {
        &:deep(.a-glyph){
            transform: none;
        }
    }
    .selector,
    .information {
        display: none;
    }
}
.product-item-wrapper {
    margin: 0 -16px;
    padding: 0 16px 40px;
    border-top: 1px solid var(--av-brand-light);
    border-bottom: 1px solid var(--av-brand-light);
    @media (--viewport-tablet) {
        margin: 0 -24px;
        padding: 0 24px 40px;
    }
}
.product-title {
    @mixin title-accent;
    padding: 16px 0;
    position: relative;
    padding-inline-end: 32px;
    color: var(--av-nav-primary);
    &:deep(.a-glyph){
        top: 24px;
        position: absolute;
        inset-inline-end: 0;
        transform: rotate(90deg);
    }
}
.message {
    @mixin paragraph-accent;
    color: #7c9b1e;
    margin-top: 8px;
    & ~ .selector {
        padding-top: 8px;
    }
}
.selector {
    gap: 24px;
    display: flex;
    flex-direction: column;
    padding-top: 16px;
    @media (--viewport-tablet) {
        padding-top: 40px;
    }
}
.selector-addon {
    margin-top: 24px;
}
.information {
    @mixin body;
    color: var(--av-fixed-secondary);
    @media (--viewport-tablet) {
        padding-top: 16px;
        padding-bottom: 16px;
    }
}
.benefit {
    @mixin caption-accent;
    color: #7c9b1e;
    margin-top: 8px;
}
.max-quantity {
    margin-top: 24px;
    position: relative;
    padding: 16px 24px;
    padding-inline-start: 48px;
    background: var(--av-fixed-success-accent);
    .a-glyph {
        top: 20px;
        inset-inline-start: 24px;
        position: absolute;
        fill: var(--av-fixed-success);
    }
}
.max-quantity-title {
    @mixin body-accent;
}
.max-quantity-item {
    @mixin caption;
    display: flex;
    color: var(--av-fixed-secondary);
    justify-content: space-between;
    &:deep(del) {
        margin-inline-end: 8px;
    }
}
.description {
    @mixin lead;
    margin: 40px 0 0;
    color: var(--av-nav-primary);
    @media (--viewport-desktop-wide) {
       width: 90%;
    }
    &:deep(b) {
        font-weight: 600;
    }
}
</style>
